import React, { useState } from 'react';
import { Mail, Check, Loader2 } from 'lucide-react';

interface FormData {
  name: string;
  email: string;
  phone: string;
  interests: string[];
}

const Newsletter: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    interests: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const interestOptions = [
    'Santo do Dia',
    'Terço e Novenas',
    'IA Devocional',
    'Jornada da Bondade',
    'Liturgia Diária',
    'Comunidade de Oração'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInterestChange = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('https://external-projects-n8n.pgrdwy.easypanel.host/webhook/ef35422f-1a14-447d-a23e-1578ba65737c', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          interests: []
        });
      } else {
        throw new Error('Erro ao enviar formulário');
      }
    } catch (err) {
      setError('Erro ao enviar formulário. Tente novamente.');
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="inscrever" className="py-20 bg-gradient-to-br from-gold-50 to-brown-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-12 shadow-xl border border-cream-200">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-3xl font-display font-bold text-brown-800 mb-4">
                Inscrição Realizada com Sucesso!
              </h3>
              <p className="text-brown-600 mb-6">
                Obrigado por se inscrever! Em breve você receberá novidades sobre o SantoGram 
                e será um dos primeiros a saber quando o app estiver disponível.
              </p>
              <button 
                onClick={() => setIsSubmitted(false)}
                className="btn-secondary"
              >
                Fazer Nova Inscrição
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="inscrever" className="py-20 bg-gradient-to-br from-gold-50 to-brown-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-gold-100 text-gold-800 rounded-full text-sm font-semibold mb-4">
              📧 Mantenha-se Conectado
            </span>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-brown-800 mb-6">
              Seja o primeiro a{' '}
              <span className="text-gradient">experimentar</span>
            </h2>
            <p className="text-xl text-brown-600 max-w-3xl mx-auto">
              Inscreva-se para receber novidades, conteúdo exclusivo e ser notificado 
              quando o SantoGram estiver disponível.
            </p>
          </div>

          {/* Form */}
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl border border-cream-200">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-brown-800 mb-2">
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-cream-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-colors"
                    placeholder="Seu nome completo"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-brown-800 mb-2">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-cream-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-colors"
                    placeholder="seu@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-brown-800 mb-2">
                  Telefone (Opcional)
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-cream-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-colors"
                  placeholder="(11) 99999-9999"
                />
              </div>

              {/* Interests */}
              <div>
                <label className="block text-sm font-semibold text-brown-800 mb-4">
                  Quais funcionalidades mais te interessam? (Escolha quantas quiser)
                </label>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {interestOptions.map((interest) => (
                    <label
                      key={interest}
                      className="flex items-center space-x-3 p-3 border border-cream-300 rounded-lg hover:bg-cream-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.interests.includes(interest)}
                        onChange={() => handleInterestChange(interest)}
                        className="w-4 h-4 text-gold-600 border-cream-300 rounded focus:ring-gold-500"
                      />
                      <span className="text-brown-700 text-sm">{interest}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              {/* Submit Button */}
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full md:w-auto min-w-[200px] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Inscrever-se Gratuitamente
                    </>
                  )}
                </button>
              </div>

              {/* Privacy Note */}
              <div className="text-center">
                <p className="text-sm text-brown-500">
                  Ao se inscrever, você concorda em receber e-mails do SantoGram. 
                  Seus dados estão seguros e você pode cancelar a qualquer momento.
                </p>
              </div>
            </form>
          </div>

          {/* Benefits */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              {
                icon: '🔔',
                title: 'Acesso Antecipado',
                description: 'Seja um dos primeiros a usar o app quando lançarmos'
              },
              {
                icon: '📖',
                title: 'Conteúdo Exclusivo',
                description: 'Receba meditações e reflexões especiais por e-mail'
              },
              {
                icon: '🎁',
                title: 'Benefícios Especiais',
                description: 'Ganhe recursos premium gratuitos no lançamento'
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-xl border border-cream-200">
                <div className="text-3xl mb-3">{benefit.icon}</div>
                <h4 className="font-display font-semibold text-brown-800 mb-2">
                  {benefit.title}
                </h4>
                <p className="text-brown-600 text-sm">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter; 